import React, { useMemo, useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiX } from 'react-icons/fi';
import './MultiSelect.css';

/**
 * Multi-select dropdown: click to open, checkboxes for each option.
 * options: Array<{ value: string, label: string }>
 * value: string[] (selected values)
 * onChange: (value: string[]) => void
 * placeholder: string
 * disabled: boolean
 */
function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select…',
  disabled = false,
  searchable = true,
  showChips = true,
  searchPlaceholder = 'Search…',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const toggle = (optValue) => {
    const next = value.includes(optValue)
      ? value.filter((v) => v !== optValue)
      : [...value, optValue];
    onChange(next);
  };

  const selectedOptions = useMemo(() => {
    const set = new Set(value);
    return options.filter((o) => set.has(o.value));
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query, searchable]);

  const summary =
    selectedOptions.length === 0 ? placeholder : selectedOptions.length === 1 ? selectedOptions[0].label : `${selectedOptions.length} selected`;

  return (
    <div ref={ref} className={`multi-select-wrap ${open ? 'multi-select-open' : ''} ${disabled ? 'multi-select-disabled' : ''}`}>
      <button
        type="button"
        className="multi-select-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="multi-select-summary">{summary}</span>
        <FiChevronDown className="multi-select-chevron" aria-hidden />
      </button>
      {showChips && selectedOptions.length > 0 && (
        <div className="multi-select-chips" aria-label="Selected items">
          {selectedOptions.map((opt) => (
            <span key={opt.value} className="multi-select-chip" title={opt.label}>
              <span className="multi-select-chip-label">{opt.label}</span>
              <button
                type="button"
                className="multi-select-chip-remove"
                onClick={() => toggle(opt.value)}
                aria-label={`Remove ${opt.label}`}
                disabled={disabled}
              >
                <FiX className="multi-select-chip-x" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="multi-select-dropdown" role="listbox">
          {searchable && (
            <div className="multi-select-search">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="multi-select-search-input"
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length === 0 ? (
            <div className="multi-select-empty">No options</div>
          ) : (
            filteredOptions.map((opt) => (
              <label key={opt.value} className="multi-select-option">
                <input
                  type="checkbox"
                  checked={value.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
